// ── El texto de un tema de ayuda: de qué fichero sale y cómo se sirve ────────
//
// Aparte de `help.js` y sin importar nada, a propósito: `help.js` lee el
// registro de módulos, que se construye con `import.meta.glob` y por tanto sólo
// existe dentro de Vite. Lo de aquí son dos reglas de decisión —qué idioma se
// enseña y qué se recorta del fichero— y las reglas se prueban sin montar nada.

/**
 * El idioma en que se enseña un tema, que no siempre es el que se pide.
 *
 * Un módulo puede llevar su explicación escrita en castellano y todavía no en
 * francés: se escriben cuatro ficheros y a veces llegan de tres en tres. La
 * respuesta a eso es el texto en OTRO idioma, no una página en blanco — quien
 * está trabajando necesita saber cómo funciona el módulo más de lo que necesita
 * leerlo en el suyo.
 *
 * Castellano primero porque es el idioma en que se escribe la documentación de
 * esta casa (§16) y por tanto el único que está siempre.
 */
const FALLBACK = ['es', 'en']

export function helpLocaleFor(topic, wanted) {
  const have = topic?.sources ?? {}
  if (have[wanted]) return wanted
  return [...FALLBACK, ...Object.keys(have)].find(one => have[one]) ?? null
}

/**
 * Los módulos que esta instancia monta, sacados de la respuesta de `/health`.
 *
 * Llegan como `crm@0.3.0`: el servidor publica ahí el número de versión porque
 * lo primero que hace un diagnóstico es comparar versiones (§10.1). Para
 * filtrar sobra, y arrastrarla sería atar la documentación a que cada módulo
 * tenga la versión que alguien espera.
 *
 * Devuelve `null` —y no un conjunto vacío— ante cualquier cosa que no sea la
 * lista esperada. Son dos respuestas distintas: vacío es «esta instancia no
 * monta ningún módulo de negocio», `null` es «no lo sé». Y es lo que evita el
 * fallo silencioso que tendría esto si `/health` cambiara de forma: la
 * documentación entera desapareciendo, sin un error en ninguna parte.
 */
export function modulesFromHealth(body) {
  if (!Array.isArray(body?.modules)) return null
  return new Set(body.modules.map(entry => String(entry).split('@')[0]).filter(Boolean))
}

/**
 * De todos los temas, los que esta instancia puede enseñar.
 *
 * Sin lista —`null`, que es «no se ha podido preguntar»— se devuelven todos,
 * que es lo que se hacía antes de poder preguntar. Aquí no se inventa una
 * respuesta: quien llama decide si enseñar eso o esperar.
 */
export function topicsFor(topics, installed) {
  if (!installed) return [...topics]
  return topics.filter(topic => !topic.module || installed.has(topic.module))
}

// ── El orden del índice ──────────────────────────────────────────────────────
//
// Los temas llegan en el orden del glob, que es el alfabético de las CARPETAS:
// `attendance, comms, construction, crm…`. En pantalla eso se lee traducido
// —«Fichajes, Radio, Obras, Clientes…»— y parece no tener orden ninguno. Y la
// plataforma salía `ai, calendar, mail, navigation…`: lo primero que encontraba
// quien empieza era la IA, y «Buscar y moverse» quedaba en mitad de la lista.
//
// El manual del cliente ya los ordenaba bien (`tools/manual/build.js`): los
// módulos por su nombre en el idioma que se lee, y la plataforma en orden de
// lectura. El índice del ERP tiene que decir lo mismo que el manual, así que el
// orden de lectura vive aquí y el test comprueba que el del manual es éste.

/** El orden en que se leen los temas de plataforma, de lo primero a lo último. */
export const PLATFORM_HELP_ORDER = [
  'navigation', 'settings', 'parameters', 'roles', 'users',
  'notifications', 'mail', 'calendar', 'ai', 'support',
]

/**
 * Los grupos del índice, ordenados: los módulos por su nombre en el idioma de
 * quien lee y la plataforma al final, con sus temas en orden de lectura.
 *
 * Dentro de un módulo no se toca nada: el tema del módulo entero va delante de
 * sus funciones sueltas, y eso ya lo decide el registro.
 */
export function orderHelpGroups(groups, locale) {
  const rank = id => {
    const at = PLATFORM_HELP_ORDER.indexOf(id)
    return at < 0 ? PLATFORM_HELP_ORDER.length : at
  }
  const business = groups
    .filter(group => group.name !== 'platform')
    .sort((a, b) => a.label.localeCompare(b.label, locale))
  const platform = groups
    .filter(group => group.name === 'platform')
    .map(group => ({
      ...group,
      topics: [...group.topics].sort((a, b) => rank(a.id) - rank(b.id) || a.id.localeCompare(b.id)),
    }))
  return [...business, ...platform]
}

/**
 * Quita el `# Título` de la primera línea.
 *
 * El fichero lo lleva porque tiene que valer como documento suelto: es lo que
 * compila el manual del cliente y lo que se lee en el repositorio. Dentro del
 * ERP el título ya lo pone el marco de la sección de ayuda, así que dejarlo
 * saldría dos veces.
 */
export function stripTitle(source = '') {
  return String(source).replace(/^\s*#\s+.*(\r?\n)+/, '')
}

/**
 * El texto de un tema en un idioma, pedido al abrirlo.
 *
 * Devuelve cadena vacía —y no lanza— cuando no hay nada: un tema sin texto es
 * una página vacía, y una excepción aquí tumbaría la sección de ayuda entera
 * por un fichero que alguien no llegó a escribir.
 */
export async function helpSourceFor(topic, wanted) {
  const lang = helpLocaleFor(topic, wanted)
  if (!lang) return ''
  try {
    return stripTitle(await topic.sources[lang]())
  } catch {
    return ''
  }
}

// ── De la clave de un glob al tema que nombra ────────────────────────────────
//
// `import.meta.glob` devuelve las claves **relativas al fichero que lo pide, y
// ya colapsadas**. Eso no es un detalle: el glob de la plataforma se escribe
// `'../../../../platform/help/*/*.md'` y las claves vuelven como
// `'../../../help/ai/es.md'` — sin el trozo `platform/`, porque quien pregunta
// ya vive dentro de `platform/`.
//
// Costó una tarde: el patrón que buscaba `platform/help/…` no casaba con
// ninguna clave, la lista de temas salía vacía y **no había error en ninguna
// parte**. Ni en la compilación —que empaqueta los `.md` igual, casen o no— ni
// en la pantalla, que enseñaba la ayuda de los módulos y ya. Por eso las dos
// lecturas viven aquí, donde se pueden probar sin Vite y contra las claves de
// verdad.

/** `…/modules/crm/help/es.md` → `{ id: 'crm', locale: 'es' }` */
export function moduleHelpKey(key) {
  const found = String(key).match(/([^/]+)\/help\/([a-z]{2})\.md$/)
  return found ? { id: found[1], locale: found[2] } : null
}

/** `…/help/ai/es.md` → `{ id: 'ai', locale: 'es' }` */
export function platformHelpKey(key) {
  const found = String(key).match(/([^/]+)\/([a-z]{2})\.md$/)
  return found ? { id: found[1], locale: found[2] } : null
}
